import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import ErrorIcon from '@mui/icons-material/Error';
import SyncIcon from '@mui/icons-material/Sync';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { type Theme, useTheme } from '@mui/material/styles';
import type React from 'react';
import { useEffect, useState } from 'react';
import { usePricing } from '../../../contexts/PricingContext';
import { useSnackbar } from '../../../contexts/SnackbarContext';
import { useTeamLimits } from '../../../hooks/useTeamLimits';
import { apiService } from '../../../services/api';
import type { Tender } from '../../../types/tender';
import { EstimatedCostChip } from '../../common/EstimatedCostChip';
import type { LoadingStates } from '../types';
import {
  calculateResetCooldown,
  extractProgressFromStatus,
  formatGenerationStatusText,
  getDocTimestampFromLink,
  getDropzoneBoxStyle,
  getLoadingKey,
  isServiceProcessingStatus,
} from '../utils';

interface ReviewActionCardProps {
  tender: Tender;
  loadingStates: LoadingStates;
  onRegenerate: (docType: string) => void;
  onDownload: (gsURI: string, fileTypeIdentifier: string, defaultFileName: string) => void;
  onReset: () => void;
  // Dropzone props
  isDragging: boolean;
  onDragEnter: (e: React.DragEvent) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDragLeave: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
  onFileUpload: (file: File) => void;
  uploadValidationWarning?: string | undefined;
  draftsCount: number;
  onUpdateStatus: (docType: string, status: string) => void;
}

const ReviewActionCard: React.FC<ReviewActionCardProps> = ({
  tender,
  loadingStates,
  onRegenerate,
  onDownload,
  onReset,
  isDragging,
  onDragEnter,
  onDragOver,
  onDragLeave,
  onDrop,
  onFileUpload,
  uploadValidationWarning,
  draftsCount,
  onUpdateStatus,
}) => {
  const theme = useTheme();
  const { pricingSettings } = usePricing();
  const { showSnackbar } = useSnackbar();
  const [resetCooldown, setResetCooldown] = useState<number>(0);
  const [errorReported, setErrorReported] = useState(false);
  const [errorDismissed, setErrorDismissed] = useState(false);
  const [lastErrorStatus, setLastErrorStatus] = useState('');
  const [confirmGenOpen, setConfirmGenOpen] = useState(false);

  const status = tender.reviewResponse?.status || '';
  const lastType = tender.reviewResponse?.lastType;
  const statusLower = status.toLowerCase();
  const isError =
    lastType !== 'quick' &&
    (statusLower.includes('error') || statusLower.includes('failed')) &&
    !statusLower.includes('complete') &&
    !statusLower.includes('closed');

  const [maxProgress, setMaxProgress] = useState(0);
  useEffect(() => {
    const currentProgress = extractProgressFromStatus(status, tender.reviewResponse?.progress) ?? 0;
    const activeProcessing = lastType !== 'quick' && isServiceProcessingStatus(status);

    if (!activeProcessing) {
      setMaxProgress(0);
    } else {
      setMaxProgress((prev) => Math.max(prev, currentProgress));
    }
  }, [status, lastType, tender.reviewResponse?.progress]);

  useEffect(() => {
    if (status !== lastErrorStatus) {
      setLastErrorStatus(status);
      if (!isError) {
        setErrorDismissed(false);
        setErrorReported(false);
      }
    }
  }, [status, isError, lastErrorStatus]);

  const handleReportError = async () => {
    try {
      await apiService.reportError({
        tenderId: tender.id || 'unknown',
        error: status,
        component: 'Review Generation',
      });
      setErrorReported(true);
      setErrorDismissed(true);
      showSnackbar('Error reported. Thank you.', 'success');
      onUpdateStatus('review', 'Error Report Complete');
    } catch (_e) {
      showSnackbar('Failed to report error.', 'error');
    }
  };

  const loadingKey = getLoadingKey(tender.id, 'regenerate', 'review');
  const isLocalLoading = !!loadingStates[loadingKey];

  const getButtonState = () => {
    const status = tender.reviewResponse?.status || '';
    const lastType = tender.reviewResponse?.lastType;
    const isProcessing =
      isLocalLoading ||
      (status &&
        lastType !== 'quick' &&
        !status.toLowerCase().includes('complete') &&
        !status.toLowerCase().includes('error') &&
        !status.toLowerCase().includes('failed'));
    const exists = !!tender.reviewFileLink;

    if (isProcessing) {
      return { text: '', showSpinner: true };
    }
    if (draftsCount === 0) {
      return { text: 'Upload Draft to Generate Review', showSpinner: false, disabled: true };
    }
    if (!exists) {
      return { text: 'Generate Review', showSpinner: false };
    }
    return { text: 'Regenerate', showSpinner: false };
  };

  const buttonState = getButtonState();

  useEffect(() => {
    const updateCooldown = () => {
      setResetCooldown(calculateResetCooldown(tender.reviewResponse, 30));
    };

    updateCooldown();
    const interval = setInterval(updateCooldown, 60000);
    return () => clearInterval(interval);
  }, [tender.reviewResponse]);
  const getRegenButtonStyle = (theme: Theme, enabled: boolean) => ({
    backgroundColor: enabled ? theme.palette.greyContrast : theme.palette.greyLight,
    color: enabled ? theme.palette.greyLight : theme.palette.greyContrast,
    border: enabled ? 'none' : `1px solid ${theme.palette.greyContrast}`,
    fontSize: '1rem',
    fontWeight: 600,
    opacity: enabled ? 1 : 0.7,
    boxShadow: theme.shadows[2],
    height: '36px',
    minHeight: '36px',
    padding: theme.spacing(0.5, 2),
    '&:hover': enabled
      ? {
          backgroundColor: theme.palette.bidlogiq?.brick || '#b73f2a',
          color: '#fff',
          boxShadow: theme.shadows[4],
        }
      : {
          backgroundColor: theme.palette.greyLight,
          boxShadow: theme.shadows[4],
        },
    '&.Mui-disabled': {
      backgroundColor: theme.palette.greyLight,
      color: theme.palette.greyContrast,
      border: `1px solid ${theme.palette.bidlogiq?.orange}`,
    },
    width: '100%',
    mb: 1,
  });

  const getDownloadButtonStyle = (theme: Theme, enabled: boolean) => ({
    backgroundColor: enabled ? theme.palette.bidlogiq.brick : theme.palette.greyLight,
    color: enabled ? theme.palette.bidlogiq.white : theme.palette.greyContrast,
    border: enabled ? 'none' : `1px solid ${theme.palette.greyContrast}`,
    fontSize: '1rem',
    fontWeight: 600,
    opacity: enabled ? 1 : 0.7,
    boxShadow: theme.shadows[2],
    height: 'auto',
    minHeight: '36px',
    padding: theme.spacing(0.5, 2),
    '&:hover': {
      backgroundColor: enabled ? theme.palette.bidlogiq.rust : theme.palette.greyLight,
      boxShadow: theme.shadows[4],
    },
    '&.Mui-disabled': {
      backgroundColor: theme.palette.greyLight,
      color: theme.palette.greyContrast,
      border: `1px solid ${theme.palette.bidlogiq.orange}`,
    },
    width: '100%',
    justifyContent: 'center',
    '& .MuiButton-label': {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      lineHeight: 1.2,
    },
  });

  const isProcessing = buttonState.showSpinner;
  const isUploading = !!loadingStates[getLoadingKey(tender.id, 'upload', 'review_draft')];
  const { checkLimit } = useTeamLimits();
  const { remaining, isLimitReached, isUnlimited } = checkLimit('reviews', tender.reviewCount || 0);

  const getRegenerateButtonText = () => {
    if (isProcessing) {
      return formatGenerationStatusText(tender.reviewResponse?.status, isLocalLoading);
    }
    if (draftsCount === 0) {
      if (isLimitReached) {
        return 'Upload Draft to Generate Review (Limit Reached)';
      }
      if (isUnlimited) {
        return 'Upload Draft to Generate Review';
      }
      return `Upload Draft to Generate Review (${remaining} remaining)`;
    }
    if (isLimitReached) {
      return `Regenerate Review (Limit Reached)`;
    }
    if (isUnlimited) {
      return `Regenerate Review`;
    }
    return `Regenerate Review (${remaining} remaining)`;
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontSize: '1rem' }}>
        Upload your draft proposal to receive a comprehensive critical review report.
      </Typography>

      {isError && !errorDismissed && (
        <Box
          sx={{
            mb: 2,
            p: 1,
            px: 2,
            borderRadius: 1,
            bgcolor: (theme) => theme.palette.error.light,
            color: (theme) => theme.palette.error.contrastText,
            border: (theme) => `1px solid ${theme.palette.error.main}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, overflow: 'hidden' }}>
            <ErrorIcon fontSize="medium" />
            <Typography variant="body1" sx={{ fontWeight: 500 }} noWrap>
              <span style={{ fontWeight: 'bold' }}>Generation Failed:</span> {status} - Please
              report to admin.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
            <Button
              variant="outlined"
              size="small"
              color="inherit"
              onClick={handleReportError}
              disabled={errorReported}
              sx={{
                whiteSpace: 'nowrap',
                bgcolor: 'white',
                color: 'error.main',
                borderColor: 'error.main',
                py: 0.5,
                px: 2,
                minHeight: '32px',
                fontSize: '1rem',
                fontWeight: 'bold',
                '&:hover': { bgcolor: 'grey.100' },
              }}
            >
              {errorReported ? 'Report Sent' : 'Notify Admin'}
            </Button>
            <IconButton
              size="small"
              onClick={() => {
                setErrorDismissed(true);
                onUpdateStatus('review', 'Error Closed');
              }}
              sx={{ color: 'inherit', p: 0.5 }}
            >
              {' '}
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      )}
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 8 }} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ flexGrow: 1 }}>
              <Button
                onClick={() => setConfirmGenOpen(true)}
                disabled={buttonState.showSpinner || isLimitReached || draftsCount === 0}
                sx={getRegenButtonStyle(
                  theme,
                  !buttonState.showSpinner && !isLimitReached && draftsCount > 0
                )}
              >
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr auto 1fr',
                    width: '100%',
                    alignItems: 'center',
                  }}
                >
                  <Box />
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}
                  >
                    {buttonState.showSpinner ? (
                      <CircularProgress
                        size={16}
                        variant="indeterminate"
                        sx={{ color: theme.palette.bidlogiq.orange }}
                      />
                    ) : (
                      <SyncIcon />
                    )}
                    <Box>{getRegenerateButtonText()}</Box>
                  </Box>
                  <Box sx={{ textAlign: 'right', minWidth: '35px' }}>
                    {buttonState.showSpinner &&
                    extractProgressFromStatus(status, tender.reviewResponse?.progress) !== undefined
                      ? `${maxProgress}%`
                      : ''}
                  </Box>
                </Box>
              </Button>
            </Box>
            {!isLimitReached && (
              <Box>
                <Tooltip
                  title="Exact billing depends on the length and complexity of the tender documents and provided context."
                  slotProps={{ tooltip: { sx: { fontSize: '0.875rem', p: 1 } } }}
                >
                  <EstimatedCostChip
                    estimate={
                      pricingSettings?.estimates?.full_draft_review || 'Est. $20.00 – $100.00'
                    }
                  />
                </Tooltip>
              </Box>
            )}
          </Box>

          <Button
            startIcon={
              loadingStates[getLoadingKey(tender.id, 'download', 'review.docx')] ? (
                <CircularProgress size={18} sx={{ color: 'bidlogiq.orange' }} />
              ) : (
                <DownloadIcon />
              )
            }
            onClick={() =>
              onDownload(
                tender.reviewFileLink || '',
                'review.docx',
                tender.reviewFileLink
                  ? tender.reviewFileLink.split('/').pop()?.split('?')[0] || 'review.docx'
                  : 'review.docx'
              )
            }
            disabled={
              !tender.reviewFileLink ||
              !!loadingStates[getLoadingKey(tender.id, 'download', 'review.docx')]
            }
            sx={getDownloadButtonStyle(theme, !!tender.reviewFileLink)}
          >
            <Box
              sx={{
                lineHeight: 1.2,
                fontWeight: 600,
                textAlign: 'center',
                fontSize: '1rem',
              }}
            >
              Download review
              {(tender.reviewFileLink || tender.reviewResponse?.startedAt) && (
                <Box
                  component="span"
                  sx={{
                    ml: 0.5,
                    fontSize: '0.75rem',
                    fontWeight: 400,
                    opacity: 0.9,
                    textTransform: 'none',
                  }}
                >
                  {`, ${getDocTimestampFromLink(tender.reviewFileLink, tender.reviewResponse)}`}
                </Box>
              )}
              <Box component="span" sx={{ textTransform: 'none' }}>
                {' (docx)'}
              </Box>
            </Box>
          </Button>

          {tender.reviewResponse?.status && lastType !== 'quick' && (
            <Box
              sx={{
                mt: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                justifyContent: 'space-between',
              }}
            >
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ flexGrow: 1, fontSize: '0.875rem' }}
              >
                Generating a review can take up to 30 minutes. Press the reset button if it is
                stuck.
              </Typography>
              <Button
                size="small"
                variant="outlined"
                color="error"
                onClick={onReset}
                disabled={resetCooldown > 0}
                sx={{
                  borderColor: 'text.error',
                  color: 'text.error',
                  whiteSpace: 'nowrap',
                }}
              >
                {resetCooldown > 0 ? `Reset (${resetCooldown}m)` : 'Reset'}
              </Button>
            </Box>
          )}
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Box sx={{ pl: { md: 2 } }}>
            <Box
              sx={{
                ...getDropzoneBoxStyle(theme, isDragging),
                p: 1, // Override padding to reduce height
                minHeight: 'auto',
                backgroundColor: isUploading ? theme.palette.action.hover : undefined,
              }}
              onDragEnter={!isUploading ? onDragEnter : undefined}
              onDragOver={!isUploading ? onDragOver : undefined}
              onDragLeave={!isUploading ? onDragLeave : undefined}
              onDrop={!isUploading ? onDrop : undefined}
            >
              <input
                type="file"
                id="review-upload-card"
                multiple
                disabled={isUploading}
                style={{ display: 'none' }}
                onChange={(e) => {
                  if (e.target.files) {
                    for (const file of Array.from(e.target.files)) {
                      onFileUpload(file);
                    }
                  }
                }}
              />
              <label
                htmlFor="review-upload-card"
                style={{ width: '100%', cursor: isUploading ? 'default' : 'pointer' }}
              >
                {isUploading ? (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      textAlign: 'center',
                      p: 0.5,
                    }}
                  >
                    <CircularProgress
                      size={24}
                      sx={{ mb: 0.5, color: theme.palette.bidlogiq.orange }}
                    />
                    <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 'medium' }}>
                      Uploading...
                    </Typography>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      textAlign: 'center',
                      p: 0.5, // Reduced internal padding
                    }}
                  >
                    <UploadFileIcon sx={{ fontSize: 32, mb: 0.5, color: 'text.secondary' }} />
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      sx={{ fontWeight: 'medium', fontSize: '1rem' }}
                    >
                      Upload your current working draft for an AI review
                    </Typography>
                  </Box>
                )}
              </label>
            </Box>{' '}
            {uploadValidationWarning && (
              <Alert
                severity="warning"
                sx={{
                  mt: 1,
                  fontSize: '0.7rem',
                  py: 0,
                  backgroundColor: 'bidlogiq.taupe',
                  color: 'bidlogiq.black',
                  '& .MuiAlert-icon': { color: 'bidlogiq.black' },
                }}
              >
                {uploadValidationWarning}
              </Alert>
            )}
          </Box>
        </Grid>
      </Grid>
      {/* Confirmation Dialog */}
      <Dialog
        open={confirmGenOpen}
        onClose={() => setConfirmGenOpen(false)}
        aria-labelledby="confirm-gen-dialog-title"
        aria-describedby="confirm-gen-dialog-description"
      >
        <DialogTitle id="confirm-gen-dialog-title">Confirm Full Draft Review</DialogTitle>
        <DialogContent>
          <DialogContentText id="confirm-gen-dialog-description">
            Generating a new review will analyze the full draft response.
            <br />
            <br />
            Do you wish to proceed?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmGenOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={() => {
              setConfirmGenOpen(false);
              onRegenerate('review');
            }}
            color="error"
            variant="contained"
            autoFocus
          >
            Yes, Generate Review
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReviewActionCard;
